import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Table,
  Button,
  Pagination,
  Form,
  InputGroup,
  Alert,
  Spinner,
  Badge,
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaBook } from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const getAuthToken = () => localStorage.getItem('authToken');

const JournalList = () => {
  const [journals, setJournals] = useState([]);
  const [filteredJournals, setFilteredJournals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const limit = 50;
  const navigate = useNavigate();

  useEffect(() => {
    fetchJournals(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = journals.filter(
        (journal) =>
          journal.journal_number?.toString().includes(searchTerm) ||
          journal.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          journal.entries?.some((entry) =>
            entry.account_name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()),
          ),
      );
      setFilteredJournals(filtered);
    } else {
      setFilteredJournals(journals);
    }
  }, [searchTerm, journals]);

  const fetchJournals = async (page) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-journals?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setJournals(response.data.journals || []);
      setFilteredJournals(response.data.journals || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching journals:', error);
      setAlert({
        show: true,
        message: 'Failed to fetch journals',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, journalNumber) => {
    if (
      !window.confirm(
        `Are you sure you want to delete journal #${journalNumber}?`,
      )
    )
      return;

    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-journal`,
        { journal_id: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setAlert({
        show: true,
        message: response.data.message || 'Journal deleted successfully!',
        variant: 'success',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
      fetchJournals(currentPage);
    } catch (error) {
      console.error('Error deleting journal:', error);
      setAlert({
        show: true,
        message: error.response?.data?.message || 'Error deleting journal',
        variant: 'danger',
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaBook style={{ fontSize: '1.8rem' }} />
                Journal Entries
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                View and manage all journal transactions
              </p>
            </div>
            <Button
              variant="light"
              onClick={() => navigate('/journal')}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaPlus /> New Journal
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Content Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Search Bar */}
          <div style={{ marginBottom: '24px' }}>
            <InputGroup style={{ maxWidth: '400px' }}>
              <InputGroup.Text
                style={{
                  backgroundColor: 'white',
                  borderRight: 'none',
                  borderRadius: '8px 0 0 8px',
                }}
              >
                <FaSearch style={{ color: '#6c757d' }} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by journal number, description, or account..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  borderLeft: 'none',
                  borderRadius: '0 8px 8px 0',
                  paddingLeft: '0',
                }}
              />
            </InputGroup>
          </div>

          {/* Loading State */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredJournals.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#6c757d',
              }}
            >
              <FaBook
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '15px' }}
              />
              <p>
                {searchTerm
                  ? 'No journals found matching your search'
                  : 'No journal entries found'}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/journal')}
                style={{
                  marginTop: '10px',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                }}
              >
                <FaPlus /> Create First Journal
              </Button>
            </div>
          ) : (
            <>
              {/* Journals Table */}
              <div style={{ overflowX: 'auto' }}>
                <Table hover responsive style={{ marginBottom: '24px' }}>
                  <thead
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: '#495057',
                          width: '10%',
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: '#495057',
                          width: '8%',
                        }}
                      >
                        No.
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: '#495057',
                          width: '42%',
                        }}
                      >
                        Entries
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: '#495057',
                          width: '25%',
                        }}
                      >
                        Description
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: '#495057',
                          width: '15%',
                          textAlign: 'center',
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJournals.map((journal) => (
                      <tr
                        key={journal.id}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                          {formatDate(journal.date)}
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                          <Badge bg="secondary">
                            #{journal.journal_number}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.85rem' }}>
                          {journal.entries && journal.entries.length > 0 ? (
                            <div
                              style={{
                                maxHeight: '150px',
                                overflowY: 'auto',
                                padding: '4px 0',
                              }}
                            >
                              {journal.entries.map((entry, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: '6px 10px',
                                    marginBottom: '4px',
                                    backgroundColor:
                                      entry.debit_credit === 'D'
                                        ? '#f8f9fa'
                                        : '#fff',
                                    borderLeft: `3px solid ${
                                      entry.debit_credit === 'D'
                                        ? '#667eea'
                                        : '#764ba2'
                                    }`,
                                    borderRadius: '4px',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <span
                                        style={{
                                          fontWeight: '500',
                                          color: '#495057',
                                        }}
                                      >
                                        {entry.account_name}
                                      </span>
                                      {entry.remark && (
                                        <div
                                          style={{
                                            fontSize: '0.8rem',
                                            color: '#6c757d',
                                            marginTop: '2px',
                                            fontStyle: 'italic',
                                          }}
                                        >
                                          {entry.remark}
                                        </div>
                                      )}
                                    </div>
                                    <span
                                      style={{
                                        fontWeight: '600',
                                        color:
                                          entry.debit_credit === 'D'
                                            ? '#667eea'
                                            : '#764ba2',
                                        marginLeft: '10px',
                                      }}
                                    >
                                      {entry.debit_credit === 'D' ? 'Dr' : 'Cr'}{' '}
                                      {formatAmount(entry.amount)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span
                              style={{ color: '#6c757d', fontStyle: 'italic' }}
                            >
                              No entries
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            color: '#495057',
                          }}
                        >
                          {journal.description || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                            }}
                          >
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() =>
                                navigate(`/edit-journal/${journal.id}`)
                              }
                              style={{
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontWeight: '600',
                              }}
                            >
                              <FaEdit /> Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleDelete(journal.id, journal.journal_number)
                              }
                              style={{
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontWeight: '600',
                              }}
                            >
                              <FaTrash /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '20px',
                  }}
                >
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default JournalList;
